/**
 * Every project in the CS run, and where the categories sit inside it.
 *
 * Lifted out of pages/CSPortfolio so the home page can show the same run without a copy
 * of the data — the portfolio page and the home page's band below the landing render the
 * same `projects`/`sections` pair through CSPortfolioComponent. Nothing here is per-page,
 * so it is all module-level: the lists are static, and the derived run is computed once
 * at import rather than on every render of either page.
 */

export const csProjects = [
  // {
  //   title: "Accessing Realism of LLM-generated Counseling Conversations",
  //   description: "Evaluates the realism of LLM-generated counseling conversations by comparing them to human-generated ones, using linguistic analysis and behavioral dynamics.",
  //   research: {
  //     university: "University of Utah",
  //     advisor: "Prof. Vivek Srikumar & Prof. Brent Kious",
  //     status: "In Progress"
  //   },
  //   media: {
  //     type: "image",
  //     url: require("../../assets/csPortfolioAssets/research/UofU/redirection_heatmap_proportions_com.webp"),
  //     alt: "Redirection Heatmap Proportions Comparison"
  //   },
  // },
  {
    title:"The Internal Clock of the Machine: A Narrative-Driven Benchmark of Temporal Distortions in LLMs",
    description: "By introducing a comparative, narrative-based benchmark grounded in established temporal illusions, this study evaluates the extent to which generative language models mimic human-like biases in temporal reasoning.",
    research: {
      university: "University of Utah",
      advisor: "Prof. Vivek Srikumar",
      funding: "Parent Fund Scholarship",
      presentations: [
        "Undergraduate Research Symposium 2026",
      ],
      status: "Submitted to AAAI 2027"
    },
    media: {
      type: "image",
      url: require("../../assets/csPortfolioAssets/research/UofU/temporal_distortions_thumbnail.webp"),
      alt: "Temporal Distortions Benchmark Thumbnail"
    },
    documents: [
      {
        name: "Poster Presentation",
        url: require("../../assets/csPortfolioAssets/research/UofU/Temporal_Distortions.webp"),
        type: "jpg",
        description: "Poster presentation of the temporal distortions benchmark at the University of Utah"
      },
    ],
    link: "https://arxiv.org/abs/2608.15394"
  },
  {
    title: "Evaluating Bias in Phoneme-Based Automatic Speech Recognition Systems: An Analysis of IPA Transcription Models",
    description: "Our analysis examines how performance varies across demographic groups and languages, providing insight into potential sources of bias and in forming the development of more inclusive phoneme-based ASR systems.",
    research: {
      university: "University of Utah",
      advisor: "Prof. Neal Patwari",
      status: "EMNLP 2026 IMPACTSPEECH ARR"
    },
    media: {
      type: "image",
      url: require("../../assets/csPortfolioAssets/research/UofU/ASR_IPA_Thumbnail.webp"),
    },
    documents: [
      {
        name: "Manuscript",
        url: "https://arxiv.org/abs/2606.11639",
        type: "pdf",
        description: "ArXiv Manuscript of the ASR Bias Analysis Project"
      }
    ],
    link: "https://arxiv.org/abs/2606.11639"
  },
  {
    title: "The Empirically Grounded Adaptive Virtual Patient for Psychotherapy Training: Disclosure That Responds to Therapist Micro-Skills",
    description: "Develop multi-session virtual patients that replicate the full therapy journey, offering more realistic practice. Using LMMs for a scalable, cost-effective training framework that helps novice mental health providers understand long-term client progress",
    research: {
      university: "Carnegie Mellon University",
      advisor: "Prof. Haiyi Zhu",
      funding: "NSF Award No. 2349558 (REU)",
      presentations: [
        "Presented at CMU HCII REU Symposium 2025",
      ],
      status: "Findings of EMNLP 2026"
    },
    media: {
      type: "video",
      url: require("../../assets/csPortfolioAssets/research/CMU/avpDemo.mp4"),
      thumbnail: require("../../assets/csPortfolioAssets/research/CMU/avpthumbnail.webp"),
      alt: "AVP Demo Video"
    },
    documents: [
      {
        name: "Poster Presentation",
        url: require("../../assets/csPortfolioAssets/research/CMU/avpPosterPresentation.pdf"),
        type: "pdf",
        description: "Poster presentation of the checklist at CMU"
      },
      {
        name: "Online Prototype",
        url: "https://multimodal-multiagent-adaptive-virt-eight.vercel.app/",
        type: "external",
        description: "Online Prototype of AVP"
      },
      {
        name: "Manuscript",
        url: "https://arxiv.org/abs/2606.10051",
        type: "pdf",
        description: "ArXiv Manuscript of the AVP Project"
      }
    ],
    link: "https://openreview.net/forum?id=oRxxdExo53&nesting=2&sort=date-desc"
  },
  {
    title: "Modeling Multi-Party Interaction in Couples Therapy: A Multi-Agent Simulation Approach",
    description: "A multimodal, multi-agent simulation system that models multi-party interactions in couples therapy, focusing on the demand-withdraw relational pattern and six interaction stages from the literature.",
    research: {
      university: "Carnegie Mellon University",
      advisor: "Prof. Haiyi Zhu",
      funding: "NSF Award No. 2349558 (REU)",
      status: "Submitted to CHI 2027"
    },
    media: {
      type: "video",
      url: require("../../assets/csPortfolioAssets/research/CMU/couplesDemo.mp4"),
      thumbnail: require("../../assets/csPortfolioAssets/research/CMU/couplesAgentThumbnail.webp"),
      alt: "Couples Therapy Demo Video"
    },
    documents: [
      {
        name: "Online Prototype",
        url: "https://couples-agent-new.onrender.com/",
        type: "external",
        description: "Online Prototype of Couples Therapy"
      },
      {
        name: "Manuscript",
        url: "https://arxiv.org/abs/2601.10970",
        type: "pdf",
        description: "ArXiv Manuscript of the Couples Therapy Project"
      }
    ],
    link: "https://arxiv.org/abs/2601.10970"
  },
  {
    title: "A Stage-Specific Checklist for Ethical AI Development: Mitigating Unintentional Harms Across the Lifecycle",
    description: "A checklist to regulate the integration of AI, focusing on promoting rights, opportunities, and access during the development and deployment stages.",
    research: {
      university: "University of Utah",
      advisor: "Prof. Vivek Srikumar",
      funding: "2X UROP (Undergraduate Research Opportunities Program)",
      presentations: [
        "Research on Capitol Hill 2025",
        "Utah Academy of Sciences, Arts & Letters Annual Conference 2026"
      ],
      status: "Manuscript Coming Soon"
    },
    media: {
      type: "image",
      url: require("../../assets/csPortfolioAssets/research/UofU/checkListPresentationThumbnail.webp"),
      alt: "Checklist Presentation Thumbnail"
    },
    documents: [
      {
        name: "Poster Presentation",
        url: require("../../assets/csPortfolioAssets/research/UofU/ROCH.pdf"),
        type: "pdf",
        description: "Poster presentation of the checklist at Research on Capitol Hill"
      },
      {
        name: "Manuscript",
        url: "",
        type: "pdf",
        description: "COMING SOON"
      }
    ],
  },
  {
    title: "Enhancing LLM Accuracy in Self-Diagnosis: Strategies for Optimizing Performance with Accessible Toolkits",
    description: "Development of an accessible toolkit to enhance the accuracy of LLMs in self-diagnosis presented at the Intermountain Healthcare Hackathon.",
    research: {
      university: "University of Minnesota",
      advisor: "Prof. Qianwen Wang",
      funding: "NSF Award Number 2349070 (REU)",
      presentations: [
        "Intermountain Healthcare Hackathon 2025",
        "UMN Research Symposium 2024"
      ],
      status: "Completed"
    },
    media: {
      type: "image",
      url: require("../../assets/csPortfolioAssets/research/UMN/umnSystemThumbnail.webp"),
      alt: "Medical Diagnosis Toolkit Thumbnail"
    },
    documents: [
      {
        name: "Intermountain Presentation",
        url: require("../../assets/csPortfolioAssets/research/UMN/llmDiagnosisSystemPresentation.pdf"),
        type: "pdf",
        description: "Presentation of the system at the Intermountain Healthcare Hackathon"
      },
      {
        name: "UMN Poster Presentation",
        url: require("../../assets/csPortfolioAssets/research/UMN/umnSystemPosterPresentation.pdf"),          
        type: "pdf",
        description: "Poster presentation of the system at the University of Minnesota"
      },
      {
        name: "Code Repository",
        url: "https://github.com/CatherineBao/healthcare-chatbot",          
        type: "external",
        description: "Github Repository for the system at the University of Minnesota"
      }
    ],
    link: "https://github.com/CatherineBao/healthcare-chatbot"
  },
  {
    title: "The (Un)Power of Words and Profile: How Language Styles and Demographics Shape LLM Performance in Medical Q&A?",
    description: "Systematically evaluate LLM performance involving healthcare-related questions with different linguistic styles and demographic details, determining how language styles and demographics shape analysis.",
    research: {
      university: "University of Minnesota",
      advisor: "Prof. Qianwen Wang",
      funding: "NSF Award Number 2349070 (REU)",
      presentations: [
        "Intermountain Healthcare Hackathon 2025"
      ],
      status: "Completed"
    },
    media: {
      type: "image",
      url: require("../../assets/csPortfolioAssets/research/UMN/umnAnalysisThumbnail.webp"),
      alt: "Medical Diagnosis Analysis Thumbnail"
    },
    documents: [
      {
        name: "Intermountain Presentation",
        url: require("../../assets/csPortfolioAssets/research/UMN/llmDiagnosisAnalysisPresentation.pdf"),
        type: "pdf",
        description: "Presentation of the analysis at the Intermountain Healthcare Hackathon"
      },
      {
        name: "Manuscript",
        url: require("../../assets/csPortfolioAssets/research/UMN/umnManuscript.pdf"),          
        type: "pdf",
        description: "Manuscript of the analysis done at the University of Minnesota (Submitted to CHI 2025)"
      }
    ],
  },
];

export const gameProjects = [
  {
    title: "Color Alchemy",
    description: "An education app that teaches the science of color mixing and theory.",
    media: {
      type: "video",
      url: require("../../assets/csPortfolioAssets/games/colorAlchemyVideo.mp4"),
      thumbnail: require("../../assets/csPortfolioAssets/games/colorAlchemyThumbnail.webp"),
      alt: "Color Alchemy Demo Video"
    },
    documents: [
      {
        name: "Code Repository",
        url: "https://github.com/CatherineBao/color-alchemy",
        type: "external",
        description: "Github Repository for Color Alchemy Project"
      },
      {
        name: "Demo Video",
        url: "https://youtu.be/kLCdcAgrGKw",
        type: "external",
        description: "Color Alchemy Demo Video"
      },
      {
        name: "Play Online",
        url: "https://robertmorelli.github.io/color-alchemy/",
        type: "external",
        description: "An online hosted version of the game"
      }
    ],
    link: "https://robertmorelli.github.io/color-alchemy/"
  },
  // {
  //   title: "Shellbound",
  //   description: "A 24 hour hackathon game made with a team of 3 people with custom art and sound track.",
  //   media: {
  //     type: "video",
  //     url: require("../../assets/csPortfolioAssets/games/shellBound.mp4"),
  //     thumbnail: require("../../assets/csPortfolioAssets/games/shellboundThumbnail.webp"),
  //     alt: "Shellbound Demo Video"
  //   },
  //   documents: [
  //     {
  //       name: "Code Repository",
  //       url: "https://github.com/CatherineBao/shellbound-web",
  //       type: "external",
  //       description: "Github Repository for Shell bound web deployment"
  //     },
  //     {
  //       name: "Play Online",
  //       url: "https://catherinebao.itch.io/shellbound",
  //       type: "external",
  //       description: "An online hosted version of the game"
  //     }
  //   ],
  //   link: "https://catherinebao.itch.io/shellbound"
  // },
];

export const technicalProjects = [
  // {
  //   title:"Modeling Structured Memory for Inter-Session Dynamics in Psychotherapy Virtual Patients",
  //   description: "Our system models the VP’s memory as a weighted, dynamically reconfigurable graph in which nodes represent experiences and edges encode typed causal and temporal relations between memories.",
  //   media: {
  //     type: "image",
  //     url: require("../../assets/csPortfolioAssets/technical/intersession_thumbnail.webp"),
  //     alt: "Memory Modeling Thumbnail"
  //   },
  //   documents: [
  //     {
  //       name: "Manuscript",
  //       url: require("../../assets/csPortfolioAssets/technical/CS6960___Modeling_Structured_Memory_for_Inter_Session_Dynamics_in_Psychotherapy_Virtual_Patients.pdf"),
  //       type: "pdf",
  //       description: "Manuscript of the Memory Modeling Project"
  //     },
  //   ],
  // },
  // {
  //   title: "Emotional Classification in Animated Media",
  //   description: "A deep learning model that classifies emotions in animated media with over an a 57% accuracy rate.",
  //   media: {
  //     type: "image",
  //     url: require("../../assets/csPortfolioAssets/technical/animationClassificationThumbnail.webp"),
  //     alt: "Confusion Matrix Thumbnail for Emotion Classification Project"
  //   },
  //   documents: [
  //     {
  //       name: "Manuscript",
  //       url: require("../../assets/csPortfolioAssets/technical/EmotionalExpression_RecognitionInAnimatedCharactersCOMP4471FinalProject.pdf"),
  //       type: "pdf",
  //       description: "Emotional Classification in Animated Media Manuscript"
  //     },
  //   ],
  // },
  {
    title: "Stained Glass Box",
    description: "A stained glass box rendered of the GPU using OpenGL and GLSL.",
    media: {
      type: "video",
      url: require("../../assets/csPortfolioAssets/technical/openGLVideo.mp4"),
      thumbnail: require("../../assets/csPortfolioAssets/technical/stainedglassThumbnail.webp"),
      alt: "Stained Glass Box Demo Video"
    },
    documents: [
      {
        name: "Code Repository",
        url: "https://github.com/CatherineBao/opengl-stained-glass-shaders",
        type: "external",
        description: "Github Repository for openGL stained glass shaders"
      },
      {
        name: "Demo Video",
        url: require("../../assets/csPortfolioAssets/technical/finalProject-3.mp4"),
        type: "external",
        description: "Color Alchemy Demo Video"
      },
    ],
    link: "https://github.com/CatherineBao/opengl-stained-glass-shaders"
  },
  // {
  //   title: "Doceode",
  //   description: "Website Entry for the 2022 Technology Student Association's (TSA) webmaster competition. (Placed 4th Nationally)",
  //   media: {
  //     type: "image",
  //     url: require("../../assets/csPortfolioAssets/technical/doceodeThumbnail.webp"),
  //     alt: "Doceode thumbnail"
  //   },
  //   documents: [
  //     {
  //       name: "Code Repository",
  //       url: "https://github.com/CatherineBao/doceodeweb",
  //       type: "external",
  //       description: "Github Repository for Doceode website"
  //     },
  //     {
  //       name: "View Online",
  //       url: "https://doceodefbla.vercel.app/",
  //       type: "external",
  //       description: "Hosted version of the website"
  //     }
  //   ],
  //   link: "https://doceodefbla.vercel.app/"
  // },
  // {
  //   title: "Sacred Images Mural",
  //   description: "Documentary Website commissioned by The Center for Documentary Arts and Expression and The Student Educational Equality Department.",
  //   media: {
  //     type: "image",
  //     url: require("../../assets/csPortfolioAssets/technical/sacredImagesThumbnail.webp"),
  //     alt: "Sacred Images Mural Documentation thumbnail"
  //   },
  //   documents: [
  //     {
  //       name: "News Article",
  //       url: "https://www.heraldextra.com/news/2022/feb/28/timpanogos-high-school-unveils-sacred-images-mural/",
  //       type: "external",
  //       description: "News Article about the mural"
  //     },
  //     {
  //       name: "View Online",
  //       url: "https://thssacredimagesmural.vercel.app/",
  //       type: "external",
  //       description: "Hosted version of the website"
  //     }
  //   ],
  //   link: "https://thssacredimagesmural.vercel.app/"
  // },
];

// Nothing labels the two runs on the page — this is the only record of where one ends
// and the next begins, and both navs plus the landing's heading are aimed by it, so the
// names are set the way that heading reads.
const groups = [
  { name: 'research', projects: csProjects },
  // Technical and games are one run: split apart they were two short bands the nav had
  // to distinguish for no gain.
  { name: 'projects', projects: [...technicalProjects, ...gameProjects] },
];

/**
 * A set of categories flattened into the pair CSPortfolioComponent takes: the run itself,
 * and where each category starts in it. Written once and used for both runs below, so the
 * home page's shorter selection gets start indices into its own run rather than into the
 * full one.
 *
 * A category that nothing is left in is dropped — an empty one is a jump the navs would
 * offer with nothing at the end of it.
 */
const runOf = (categories) => {
  let cursor = 0;
  const sections = categories
    .map((category) => {
      const startIndex = cursor;
      cursor += category.projects.length;
      return { name: category.name, startIndex, count: category.projects.length };
    })
    .filter((section) => section.count > 0)
    .map(({ name, startIndex }) => ({ name, startIndex }));

  return { projects: categories.flatMap((category) => category.projects), sections };
};

// The whole run, flattened, in category order, and where each category begins in it.
export const { projects, sections } = runOf(groups);

// What the home page's band shows instead of all of it: the opening stretch of the
// research and the built things at the end of the run. Enough to read the work from the
// page someone lands on without the band turning into the portfolio page — the whole run
// is a click away at /cs-portfolio. Counted off the flattened run rather than per
// category, so the split between them moves on its own as projects are added or
// commented out.
const HOME_LEADING = 4;
const HOME_TRAILING = 2;

const homeGroups = (() => {
  const total = projects.length;
  let cursor = 0;
  return groups.map((group) => {
    const start = cursor;
    cursor += group.projects.length;
    return {
      name: group.name,
      projects: group.projects.filter((_, offset) => {
        const index = start + offset;
        return index < HOME_LEADING || index >= total - HOME_TRAILING;
      }),
    };
  });
})();

export const { projects: homeProjects, sections: homeSections } = runOf(homeGroups);
